/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

/* eslint-disable @atlaskit/design-system/no-css-tagged-template-expression */
// See https://hello.atlassian.net/wiki/spaces/EDAI/pages/4834594169/LDR+Keeping+Emotion+Global+usages
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, Global, jsx } from '@emotion/react';

import { getSelectionStyles, SelectionStyle } from '@atlaskit/editor-shared-styles';
import { fg } from '@atlaskit/platform-feature-flags';
import { N200, N700, N800 } from '@atlaskit/theme/colors';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';

import { InlineSuggestionToolbar } from '../../../pm-plugins/inline-suggestions/floating-toolbar/floating-toolbar';
import { SuggestionManager } from '../../../pm-plugins/inline-suggestions/handlers/suggestion-manager';

const highlightStyles = css`
	background-color: ${token('color.background.accent.blue.subtlest', '#E9F2FF')};
	border-bottom: ${token('border.width.selected')} solid
		${token('color.background.accent.blue.subtler', '#cce0ff')};
`;

const nodeHighlightStyles = css`
	border: ${token('border.width')} solid ${token('color.background.accent.blue.subtle', '#579DFF')};
	box-shadow: 0 0 2px 1px ${token('color.background.accent.blue.subtler', '#cce0ff')};
`;

const cellHighlightStyles = css`
	${nodeHighlightStyles}
	::after {
		border-left: ${token('border.width')} solid
			${token('color.background.accent.blue.subtle', '#579DFF')};
		border-bottom: ${token('border.width')} solid
			${token('color.background.accent.blue.subtle', '#579DFF')};
	}
`;

// override standard dropdown max-height 90vh to accomodate for long list of translation languages
const translationsFloatingToolbarDropdownStyles = css`
	[aria-label='Annotation toolbar'],
	[aria-label='Selection toolbar'] {
		[data-role='droplistContent'] {
			max-height: 400px;
			overflow-x: hidden;
			[data-testid^='ak-editor-ai-selection-toolbar-translate-button']
				[data-item-elem-before]
				> span {
				padding: 0;
			}
		}
		[data-testid='ak-editor-ai-selection-toolbar-ai-dropdown'] > span > span {
			/* override negative margin on dropdown button */
			margin: 0;
		}
	}
`;

const dropdownHeadingStyles = css`
	[aria-label='Selection toolbar'] {
		[data-testid^='ak-editor-ai-selection-toolbar-dropdown-heading'] {
			cursor: default;
			> span {
				gap: initial;
			}
			[data-item-elem-before] > span {
				padding: 0;
			}

			[data-item-title] {
				text-transform: uppercase;
				font-size: 0.75rem;
				font-weight: ${token('font.weight.bold')};
				color: ${token('color.text', N200)};
			}
		}
		[data-testid^='ak-editor-ai-selection-toolbar-dropdown-heading']:hover {
			color: ${token('color.text', N700)};
		}
	}
`;

const rovoAgentsFloatingToolbarDropdownStyles = css`
	[aria-label='Annotation toolbar'],
	[aria-label='Selection toolbar'] {
		[data-role='droplistContent'] {
			[data-testid='ak-editor-ai-selection-toolbar-dropdown-view-all-agents']:not(:only-child) {
				border-block-start: ${token('border.width.selected')} solid
					var(--ds-menu-seperator-color, ${token('color.border', '#e1e3e7')});
				> span {
					gap: 4px;
				}
			}

			button[data-testid^='ak-editor-ai-selection-toolbar-dropdown-agent']
				[data-item-title='true']
				> span {
				white-space: normal;
			}
		}
	}
`;

const aiSelectionToolbarButtonStyles = () => {
	if (editorExperiment('platform_editor_controls', 'variant1')) {
		// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
		return css`
			/* Reduce ai button padding as per designs */
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'],
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:disabled,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:hover,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:focus {
				padding: 0px ${token('space.075', '6px')};
			}

			/* Reduce annotation button padding as per designs */
			button[data-testid='ak-editor-annotation-toolbar-create-button'],
			button[data-testid='ak-editor-annotation-toolbar-create-button']:disabled,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:hover,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:focus {
				padding: 0px ${token('space.075', '6px')};
				span:first-child {
					margin: 0;
				}
				span[role='img'],
				svg {
					padding: 0;
				}
			}
		`;
	} else {
		return css`
			/* Reduce ai button padding as per designs */
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'],
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:disabled,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:hover,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:focus,
			/* Reduce annotation button padding as per designs */
			button[data-testid='ak-editor-annotation-toolbar-create-button'],
			button[data-testid='ak-editor-annotation-toolbar-create-button']:disabled,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:hover,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:focus {
				padding: 0px ${token('space.100', '8px')};
			}

			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'] svg {
				padding-right: ${token('space.050', '4px')};
			}

			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'] > span:only-child {
				svg {
					padding: 0px ${token('space.100', '8px')};
				}
			}
		`;
	}
};

const inlineSuggestionsStyles = () => {
	return css`
		.ak-editor-content-area .${InlineSuggestionToolbar.INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS} {
			padding: ${token('space.100', '8px')};
			border-radius: ${token('space.075', '6px')};

			/* Reset default margin on date lozenge elements to ensure padding is applied as intended */
			[class$='-DateLozenge'] {
				margin: 0;
			}
		}
		.${SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.underline} {
			text-decoration-line: underline;
			text-decoration-style: dashed;
			text-decoration-color: ${token('color.border.accent.blue', '#1D7AFC')};
		}

		.${SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.highlight} {
			background-color: ${token('color.background.selected', '#E9F2FF')};
		}

		.${SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.completionHint} {
			color: ${token('color.text', N800)};
			opacity: ${token('opacity.disabled', '0.5')};
			pointer-events: none;
			user-select: none;
		}
	`;
};

const oldUserHighlightBackgroundColor = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.akEditor div.ProseMirror': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.ai-pre-stream-range-selection': {
			backgroundColor: `rgb(from ${token('color.background.accent.blue.subtlest.pressed')} r g b / 0.5)`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.ak-editor-annotation-draft, &.ak-editor-annotation-focus, &.ak-editor-annotation-blur': {
				all: 'unset',
				backgroundColor: `rgb(from ${token('color.background.accent.blue.subtlest.pressed')} r g b / 0.5)`,
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.annotationView-content-wrap': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'.ak-editor-annotation-draft, .ak-editor-annotation-focus, .ak-editor-annotation-blur': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
				'&:has(.ai-pre-stream-selection-highlight)': {
					all: 'unset',
					backgroundColor: `rgb(from ${token('color.background.accent.blue.subtlest.pressed')} r g b / 0.5)`,
				},
			},
		},
	},
});

const userHighlightBackgroundColor = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.akEditor div.ProseMirror': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.ai-pre-stream-range-selection': {
			backgroundColor: 'Highlight',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.ak-editor-annotation-draft, &.ak-editor-annotation-focus, &.ak-editor-annotation-blur': {
				all: 'unset',
				backgroundColor: 'Highlight',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.annotationView-content-wrap': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'.ak-editor-annotation-draft, .ak-editor-annotation-focus, .ak-editor-annotation-blur': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
				'&:has(.ai-pre-stream-selection-highlight)': {
					all: 'unset',
					backgroundColor: 'Highlight',
				},
			},
		},
	},
});

const inEditorStreamingStyles = css`
	.akEditor div.ProseMirror {
		.ai-modal-mount-point-widget {
			bottom: 0;
		}
		tr > .ai-modal-mount-point-widget {
			display: table-cell;
		}
		.ai-pre-stream-empty-selection-caret {
			content: '';
			position: relative;
			border-left: ${token('border.width')} solid ${token('color.text')};
			margin-left: -1px;
			left: 0.5px;
		}
		.ai-inline-streaming-hidden {
			display: none;
		}
		.ai-in-editor-user-content {
			opacity: 0.2;
		}
		.ai-in-editor-ai-content:not(
			.ak-editor-annotation-draft,
			.ak-editor-annotation-focus,
			.ak-editor-annotation-blur
		) {
			background-color: ${token('color.background.accent.purple.subtlest')};
			border-bottom: ${token('border.width')} dashed ${token('color.border.accent.purple')};
		}
		.ai-in-editor-telepointer {
			position: relative;
			z-index: 1;
			& > .ai-in-editor-telepointer-label {
				position: absolute;
				box-sizing: border-box;
				display: flex;
				flex-direction: row;
				justify-content: center;
				align-items: center;
				padding: ${token('space.025')};
				gap: ${token('space.075')};
				height: 16px;
				top: ${token('space.0')};
				left: ${token('space.0')};
				border-radius: 0px ${token('space.050')} ${token('space.050')} 0px;
				border: solid ${token('border.width')} transparent;
				background:
					linear-gradient(${token('color.text')}, ${token('color.text')}) padding-box,
					conic-gradient(
							from 270deg at 50% 50%,
							#1868db 0deg,
							#1868db 115deg,
							#fca700 115deg,
							#fca700 180deg,
							#bf63f3 180deg,
							#bf63f3 310deg,
							#82b536 310deg,
							#82b536 359.96deg,
							#1868db 360deg
						)
						border-box;

				font-family: ${token('font.family.body')};
				font-weight: ${token('font.weight.semibold')};
				font-size: 10px;
				line-height: 9px;
				text-align: center;
				white-space: pre;
				color: ${token('color.text.inverse')};
			}
			&::after {
				content: '';
				position: absolute;
				width: 1px;
				height: 24px;
				top: ${token('space.0')};
				left: ${token('space.0')};
				background: ${token('color.border.brand')};
			}
		}
	}
`;

const globalStyles = () => {
	return css`
		.akEditor div.ProseMirror {
			.ai-modal-start-widget,
			.ai-modal-end-widget {
				position: relative;

				&::before {
					content: '';
					background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMyIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDMgMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMSAxSDBMMSAxLjg1NzE0VjE4LjE0MzNMMCAxOS4wMDA0SDNMMiAxOC4xNDMzVjEuODU3MTRMMyAxSDJIMVoiIGZpbGw9IiM1NzlERkYiLz4KPHJlY3QgeT0iMTkiIHdpZHRoPSIzIiBoZWlnaHQ9IjEiIGZpbGw9IiM1NzlERkYiLz4KPHJlY3Qgd2lkdGg9IjMiIGhlaWdodD0iMSIgZmlsbD0iIzU3OURGRiIvPgo8L3N2Zz4K');
					z-index: 1;
					display: block;
					position: absolute;
					top: 0;
					bottom: ${token('space.negative.025', '-2px')}; /* match highlight height + border */
					background-repeat: no-repeat;
					background-position-x: center;
					background-position-y: center;
					background-size: contain;
					aspect-ratio: 3/20; /* svg (width / height) */
				}
			}

			.ai-modal-start-widget::before {
				left: 0px;
				margin-left: -0.1em;
			}

			.ai-modal-end-widget::before {
				right: 0px;
				margin-right: -0.1em;
			}

			.ai-generated-content-highlight {
				${highlightStyles}
			}

			.ai-selection-highlight {
				${highlightStyles}

				/* selection from the right */
				&.ak-editor-annotation-draft,
				&.ak-editor-annotation-focus,
      			&.ak-editor-annotation-blur {
					all: unset;

					${highlightStyles}
				}
			}

			.ai-selection-node .pm-table-wrapper {
				table tr td,
				table tr th {
					${getSelectionStyles([SelectionStyle.Border, SelectionStyle.Blanket])}
				}
			}

			.ai-generated-node-highlight .pm-table-wrapper {
				table tr td,
				table tr th {
					${cellHighlightStyles}
				}
			}

			// selection from the left
			.annotationView-content-wrap {
				.ak-editor-annotation-draft,
				.ak-editor-annotation-focus,
				.ak-editor-annotation-blur {
					&:has(.ai-selection-highlight) {
						all: unset;

						${highlightStyles}
					}
				}
			}
		}
	`;
};

const nestedEditorStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.ak-editor-plugin-ai-modal .ProseMirror': {
		/**
		 * Minimum height is set for confluence comment editor
		 * when Editor AI is invoked in comments. So overriding it here.
		 */
		minHeight: 'auto',
	},
});

export const GlobalWrapper = ({ children }: { children: React.ReactNode }) => {
	return (
		<React.Fragment>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
			<Global
				styles={[
					translationsFloatingToolbarDropdownStyles,
					rovoAgentsFloatingToolbarDropdownStyles,
					inlineSuggestionsStyles(),
					dropdownHeadingStyles,
					globalStyles(),
					inEditorStreamingStyles,
					fg('platform_editor_ai_aifc_patch_beta_2')
						? userHighlightBackgroundColor
						: oldUserHighlightBackgroundColor,
					aiSelectionToolbarButtonStyles(),
					fg('platform_editor_aifc_in_page_comment') && nestedEditorStyles,
				]}
			/>
			{children}
		</React.Fragment>
	);
};
